/**
 * A PALETA DE BUSCA — `⌘K` / `Ctrl+K` (Story 18.1, PR B, AC-4 a AC-9).
 *
 * A forma é a VARIANTE C do desenho aprovado (`design/explorations/global-search/`):
 * trilho de escopo à esquerda, gatilho em forma de campo no trilho da app,
 * linha de 44px, âncora alta (11vh), 760px de largura, glifo da entidade,
 * rodapé de atalhos ligado, e **nenhum mérito na linha**. São parâmetros
 * CONTRATUAIS do owner (README do epic 18) — mudá-los é mudar o epic.
 *
 * ────────────────────────────────────────────────────────────────────────
 * AS SEIS DECISÕES QUE NÃO SE VEEM NO ECRÃ
 * ────────────────────────────────────────────────────────────────────────
 *
 * 1. ⚠️ O `<dialog>` É ESCRITO AQUI, E NÃO É O `components/ui/dialog.tsx`.
 *    O primitivo partilhado fixa `m-auto` e `max-w-md` no próprio elemento, e
 *    não aceita `className` — as duas coisas contradizem, de frente, os dois
 *    parâmetros contratuais desta tela (760px e âncora a 11vh). Alargar o
 *    primitivo para um caso que não é o dele mudaria o vocabulário de ~10
 *    diálogos de formulário por causa de um overlay. O que se REUSA dele é o
 *    padrão, linha a linha: `showModal()` com degradação para o atributo `open`
 *    (o jsdom não implementa `showModal`), o listener de `close` para o `Esc`
 *    nativo, e o clique no próprio elemento como clique no véu.
 *
 * 2. ⚠️ NENHUM `backdrop-blur-*` DO TAILWIND NO MESMO ELEMENTO QUE `glass-float`.
 *    As duas declarações de `backdrop-filter` fazem o Lightning CSS colapsá-las
 *    e servir só a prefixada, que o Chrome ignora: o resultado é
 *    `backdrop-filter: none` e o vidro desaparece sem erro nenhum. O véu usa a
 *    variante `backdrop:` — que gera regra para o pseudo-elemento `::backdrop`,
 *    outra regra, sem colisão. É o que `components/ui/dialog.tsx` já faz.
 *
 * 3. ⚠️ NADA DE `scrollIntoView`. Esse método rola o ancestral mais próximo que
 *    possa rolar e, num overlay, empurra a PÁGINA por baixo do véu. Aqui
 *    mede-se o `offsetTop` da linha e ajusta-se o `scrollTop` só do scroller da
 *    lista.
 *
 * 4. ⚠️ O ESQUELETO NÃO USA `@utility skeleton`. O gradiente dela interpola até
 *    `--panel-hover`, que é BRANCO PURO no mundo claro; sobre `glass-float`
 *    isso é branco sobre branco e a caixa lê-se como VAZIA em vez de a
 *    carregar. Aqui o gradiente vai de `--hl-fill` a `--hair-strong`, os dois
 *    valores de realce que a app já decidiu e que invertem sozinhos entre os
 *    mundos. Nenhum valor novo inventado.
 *
 * 5. ⚠️ UM GRUPO COM `failed: true` MOSTRA ERRO, NUNCA "0". Dizer zero sobre uma
 *    entidade que não chegou a responder é afirmar o que não se sabe — e é
 *    exactamente o defeito que o campo `failed` existe para evitar.
 *
 * 6. ⚠️ O `SearchField` (`components/lumen/search-field.tsx`) NÃO É REUSADO. Ele
 *    é a busca de TOOLBAR: cápsula de 220px fixos, sem `role`, sem lista sob o
 *    seu comando. O campo desta paleta é `role="combobox"`, ocupa a largura do
 *    diálogo, e comanda uma `listbox` por `aria-controls` e
 *    `aria-activedescendant`. Reusá-lo obrigaria a alargar o contrato dele para
 *    um caso que não é o dele — e o custo cairia nas ~8 toolbars que o usam.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowDown,
  ArrowUp,
  Clock,
  CornerDownLeft,
  Inbox,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react'
import {
  SEARCH_KINDS,
  flattenGroups,
  resolveSearchIds,
  searchGlobal,
  type SearchEnvelope,
  type SearchGroup,
  type SearchItem,
  type SearchKind,
} from '@/lib/api/global-search'
import {
  clearSearchHistory,
  forgetSearchRecord,
  orderByHistory,
  readSearchHistory,
  rememberSearchRecord,
  searchItemRef,
  type SearchRef,
} from '@/lib/search-history'
import { SEARCH_KIND_META, SearchResultRow } from '@/components/global-search/search-result-row'
import { searchShortcutLabel } from '@/hooks/use-global-search-hotkey'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useGlobalSearchStore } from '@/stores/global-search-store'
import { cn } from '@/lib/utils'

/**
 * 250ms — o valor que ESTA app usa para busca no SERVIDOR
 * (`hooks/use-debounced-value.ts`; os 150ms do mesmo ficheiro são de filtro
 * local, que não custa rede). É também o número de que o teto de 240/min da
 * rota foi derivado: 4 pedidos por segundo é o máximo físico deste campo.
 */
const DEBOUNCE_MS = 250

/** Folga ao rolar: o cabeçalho do grupo tem de vir com a primeira linha dele. */
const FOLGA_DO_CABECALHO = 34

const LISTBOX_ID = 'gs-listbox'

type Estado = 'idle' | 'loading' | 'ready' | 'error'

/** O grupo `application`, se existir e tiver itens — é a preferência do produto. */
function indiceDaPreferencia(plana: readonly SearchItem[]): number {
  const i = plana.findIndex((item) => item.kind === 'application')
  return i === -1 ? 0 : i
}

export function GlobalSearchDialog(): ReactElement {
  const isOpen = useGlobalSearchStore((s) => s.isOpen)
  const close = useGlobalSearchStore((s) => s.close)
  const navigate = useNavigate()

  const [term, setTerm] = useState('')
  const [scope, setScope] = useState<SearchKind | null>(null)
  const [i, setI] = useState(0)
  const [estado, setEstado] = useState<Estado>('idle')
  const [envelope, setEnvelope] = useState<SearchEnvelope | null>(null)
  const [recentRefs, setRecentRefs] = useState<SearchRef[]>([])
  /** Bump = "vai buscar outra vez" — abrir a paleta e o "Tentar de novo". */
  const [tentativa, setTentativa] = useState(0)

  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  /**
   * As referências com que o pedido de "Recentes" é feito.
   *
   * Vive num `ref` e não nas dependências do efeito de propósito: ESQUECER um
   * recente muda a lista guardada, e se isso disparasse um pedido novo a lista
   * piscava e o servidor era chamado para saber menos do que já se sabe. O
   * esquecimento é resolvido no cliente, sobre o que já veio.
   */
  const refsDoPedido = useRef<SearchRef[]>([])

  const debounced = useDebouncedValue(term, DEBOUNCE_MS)
  const termoActivo = debounced.trim()
  const temTermo = termoActivo.length > 0

  // ── abrir: começa sempre limpo, e relê o histórico do disco ──────────────
  useEffect(() => {
    if (!isOpen) return
    const refs = readSearchHistory()
    refsDoPedido.current = refs
    setRecentRefs(refs)
    setTerm('')
    setScope(null)
    setI(0)
    setEnvelope(null)
    setEstado('idle')
    setTentativa((n) => n + 1)
  }, [isOpen])

  // ── o `<dialog>` nativo ──────────────────────────────────────────────────
  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    if (isOpen && !el.open) {
      // O jsdom não implementa `showModal` — degrada para o atributo, que é o
      // que permite consultar o conteúdo nos testes.
      if (typeof el.showModal === 'function') el.showModal()
      else el.setAttribute('open', '')
    } else if (!isOpen && el.open) {
      if (typeof el.close === 'function') el.close()
      else el.removeAttribute('open')
    }
  }, [isOpen])

  useEffect(() => {
    const el = dialogRef.current
    if (!el) return
    // O `Esc` do browser fecha o `<dialog>` sem passar pelo React: sem este
    // listener o elemento fechava e a loja continuava a dizer "aberto".
    const aoFechar = () => close()
    el.addEventListener('close', aoFechar)
    return () => el.removeEventListener('close', aoFechar)
  }, [close])

  useEffect(() => {
    if (!isOpen) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [isOpen])

  // ── o pedido, com CANCELAMENTO a cada tecla ──────────────────────────────
  //
  // ⚠️ O `AbortController` não é higiene: sem ele, duas respostas em voo podem
  // chegar fora de ordem e a mais LENTA (de um termo mais antigo) repinta a
  // lista por cima da mais recente. A pessoa vê resultados de "mar" depois de
  // ter escrito "marina", e não há nada no ecrã que explique porquê.
  useEffect(() => {
    if (!isOpen) return

    const controlador = new AbortController()
    const refs = refsDoPedido.current

    if (!temTermo && refs.length === 0) {
      setEnvelope(null)
      setEstado('idle')
      return
    }

    setEstado('loading')
    const pedido = temTermo
      ? searchGlobal(termoActivo, controlador.signal)
      : resolveSearchIds(refs, controlador.signal)

    pedido
      .then((resposta) => {
        if (controlador.signal.aborted) return
        setEnvelope(resposta)
        setEstado('ready')
      })
      .catch((erro: unknown) => {
        if (controlador.signal.aborted) return
        if (erro instanceof Error && erro.name === 'AbortError') return
        setEstado('error')
      })

    return () => controlador.abort()
  }, [isOpen, termoActivo, temTermo, tentativa])

  // ── o que se desenha ─────────────────────────────────────────────────────
  const grupos: SearchGroup[] = useMemo(() => envelope?.groups ?? [], [envelope])

  const recentes: SearchItem[] = useMemo(
    () => (temTermo ? [] : orderByHistory(recentRefs, flattenGroups(grupos))),
    [temTermo, recentRefs, grupos],
  )

  /** Os grupos com conteúdo, já filtrados pelo escopo — na ordem do servidor. */
  const gruposVisiveis = useMemo(
    () => grupos.filter((g) => g.items.length > 0 && (scope === null || g.kind === scope)),
    [grupos, scope],
  )

  const plana: SearchItem[] = useMemo(
    () => (temTermo ? flattenGroups(gruposVisiveis) : recentes),
    [temTermo, gruposVisiveis, recentes],
  )

  const activo = plana[i] ?? null
  const escopoFalhou = scope !== null && (grupos.find((g) => g.kind === scope)?.failed ?? false)
  const mostraEstado = estado === 'loading' || estado === 'error' || escopoFalhou

  /**
   * Um `Enter` sem tocar nas setas abre O PRIMEIRO ITEM DO GRUPO `application`,
   * na ordem que o servidor mandou — é isto, e só isto, que "preferência para
   * candidatura" significa.
   *
   * Reposto a cada termo e a cada escopo: manter o índice faria a seta apontar
   * para um registo diferente do que estava sob ela um instante antes.
   */
  useEffect(() => {
    setI(indiceDaPreferencia(plana))
    // `plana.length` e não `plana`: o array é recriado a cada render e usá-lo
    // como dependência repunha o índice a meio de uma navegação por teclado.
  }, [termoActivo, scope, plana.length])

  /**
   * Trazer a linha activa à vista SEM `scrollIntoView` (ver o cabeçalho, §3).
   */
  useEffect(() => {
    const caixa = bodyRef.current
    if (!caixa) return
    const linha = caixa.querySelector<HTMLElement>(`[data-idx="${i}"]`)
    if (!linha) return
    const topo = linha.offsetTop
    const fundo = topo + linha.offsetHeight
    const vistaTopo = caixa.scrollTop
    const vistaFundo = vistaTopo + caixa.clientHeight
    if (topo < vistaTopo + FOLGA_DO_CABECALHO) {
      caixa.scrollTop = Math.max(0, topo - FOLGA_DO_CABECALHO)
    } else if (fundo > vistaFundo) {
      caixa.scrollTop = fundo - caixa.clientHeight
    }
  }, [i, plana])

  // ── acções ───────────────────────────────────────────────────────────────
  const abrir = useCallback(
    (item: SearchItem) => {
      // Grava ANTES de navegar: o diálogo desmonta a seguir, e uma gravação
      // depois do `navigate` fica pendurada num componente que já não existe.
      setRecentRefs(rememberSearchRecord(item))
      close()
      navigate(item.path)
    },
    [close, navigate],
  )

  // Esquecer NÃO refaz o pedido: a lista encolhe no cliente, sobre o que já
  // veio. O índice do teclado é reposto pelo efeito da preferência, que observa
  // o comprimento da lista.
  const esquecer = useCallback((item: SearchItem) => {
    setRecentRefs(forgetSearchRecord(searchItemRef(item)))
  }, [])

  const sairPara = useCallback(
    (base: string) => {
      close()
      navigate(`${base}?q=${encodeURIComponent(termoActivo)}`)
    },
    [close, navigate, termoActivo],
  )

  function aoTeclar(e: ReactKeyboardEvent<HTMLDivElement>) {
    const total = plana.length

    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (total) setI((n) => (n + 1) % total)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (total) setI((n) => (n - 1 + total) % total)
      return
    }
    if (e.key === 'Home' && total) {
      e.preventDefault()
      setI(0)
      return
    }
    if (e.key === 'End' && total) {
      e.preventDefault()
      setI(total - 1)
      return
    }
    if (e.key === 'Enter' && activo) {
      e.preventDefault()
      abrir(activo)
      return
    }
    // ⚠️ A PRECEDÊNCIA DO `⌫` COM O CAMPO VAZIO, ESCRITA (AC-6):
    //   (1) há escopo activo  → tira o escopo;
    //   (2) senão, há um RECENTE realçado → esquece-o;
    //   (3) senão, nada.
    // Sem esta ordem, os dois donos do `⌫` disparam ao mesmo tempo: tirar um
    // filtro e apagar um registo do histórico com a mesma tecla, na mesma
    // pancada.
    if (e.key === 'Backspace' && term.length === 0) {
      if (scope !== null) {
        e.preventDefault()
        setScope(null)
        return
      }
      if (!temTermo && activo) {
        e.preventDefault()
        esquecer(activo)
        return
      }
      return
    }
    // ⌘1…⌘6 salta para o escopo, NA ORDEM DAS ENTIDADES — ⌘1 é sempre
    // Candidaturas. Sem `preventDefault` o browser troca de aba.
    if ((e.metaKey || e.ctrlKey) && /^[1-6]$/.test(e.key)) {
      e.preventDefault()
      const alvo = SEARCH_KINDS[Number(e.key) - 1]
      if (alvo) setScope((s) => (s === alvo ? null : alvo))
    }
  }

  // ── o trilho de escopo (variante C) ──────────────────────────────────────
  function contagemDe(grupo: SearchGroup | undefined): string | null {
    if (!temTermo || !grupo || grupo.failed) return null
    return grupo.hasMore ? `${grupo.items.length}+` : String(grupo.items.length)
  }

  const trilho = (
    <nav
      aria-label="Filtrar por entidade"
      className="border-hair relative max-h-[min(58vh,520px)] overflow-y-auto border-r p-1.5"
    >
      <button
        type="button"
        aria-pressed={scope === null}
        onClick={() => setScope(null)}
        className={cn(
          'text-ink-2 hover:text-ink hover:bg-sunk flex min-h-[32px] w-full items-center gap-[9px] rounded-sm px-[9px] py-1.5 text-[12.5px] leading-tight',
          scope === null && 'text-ink bg-panel-hover shadow-xs font-semibold',
        )}
      >
        <Search size={14} aria-hidden="true" />
        Tudo
        {temTermo ? (
          <span className="text-ink-4 ml-auto font-mono text-[10.5px]">
            {flattenGroups(grupos).length}
          </span>
        ) : null}
      </button>

      {SEARCH_KINDS.map((kind, n) => {
        const grupo = grupos.find((g) => g.kind === kind)
        const falhou = grupo?.failed ?? false
        const vazio = temTermo && !falhou && (grupo?.items.length ?? 0) === 0
        const contagem = contagemDe(grupo)
        const Icon = SEARCH_KIND_META[kind].icon
        return (
          <button
            key={kind}
            type="button"
            aria-pressed={scope === kind}
            disabled={vazio}
            title={searchShortcutLabel().replace('K', String(n + 1))}
            onClick={() => setScope((s) => (s === kind ? null : kind))}
            className={cn(
              'text-ink-2 hover:text-ink hover:bg-sunk flex min-h-[32px] w-full items-center gap-[9px] rounded-sm px-[9px] py-1.5 text-[12.5px] leading-tight',
              // Um escopo sem resultados fica LEGÍVEL e não clicável — o
              // precedente do `.seg`: escondê-lo deixaria a pessoa sem saber
              // que a entidade existe.
              'disabled:hover:text-ink-3 disabled:opacity-40 disabled:hover:bg-transparent',
              scope === kind && 'text-ink bg-panel-hover shadow-xs font-semibold',
            )}
          >
            <Icon size={14} aria-hidden="true" />
            {SEARCH_KIND_META[kind].label}
            {falhou ? (
              // ⚠️ ERRO, e NUNCA "0 desativado": a entidade não chegou a
              // responder, e afirmar zero sobre isso seria inventar.
              <span
                className="text-error ml-auto inline-flex items-center"
                title={`Não deu para buscar em ${SEARCH_KIND_META[kind].label}`}
                aria-label={`Não deu para buscar em ${SEARCH_KIND_META[kind].label}`}
              >
                <TriangleAlert size={13} aria-hidden="true" />
              </span>
            ) : contagem !== null ? (
              <span className="text-ink-4 ml-auto font-mono text-[10.5px]">{contagem}</span>
            ) : null}
          </button>
        )
      })}
    </nav>
  )

  // ── o corpo ──────────────────────────────────────────────────────────────
  const esqueleto = (
    <div className="p-1.5" aria-hidden="true">
      {[68, 52, 74, 44, 60].map((largura, n) => (
        <div key={n} className="flex min-h-[44px] items-center gap-[11px] px-2.5">
          <span className="animate-shimmer rounded-xs h-[26px] w-[26px] shrink-0 bg-[linear-gradient(90deg,var(--hl-fill),var(--hair-strong),var(--hl-fill))] bg-[length:200%_100%]" />
          <span className="min-w-0 flex-1">
            <span
              className="animate-shimmer block h-[9px] rounded-full bg-[linear-gradient(90deg,var(--hl-fill),var(--hair-strong),var(--hl-fill))] bg-[length:200%_100%]"
              style={{ width: `${largura}%` }}
            />
            <span
              className="animate-shimmer mt-[5px] block h-[7px] rounded-full bg-[linear-gradient(90deg,var(--hl-fill),var(--hair-strong),var(--hl-fill))] bg-[length:200%_100%]"
              style={{ width: `${Math.max(24, largura - 26)}%` }}
            />
          </span>
        </div>
      ))}
    </div>
  )

  const vazioComSaida = (
    <div className="flex flex-col items-center gap-[7px] px-[22px] pb-[30px] pt-[34px] text-center">
      <span className="text-ink-4 mb-[3px]">
        <Inbox size={22} aria-hidden="true" />
      </span>
      <p className="text-ink text-base font-medium">Nada encontrado para «{termoActivo}»</p>
      <p className="text-ink-3 max-w-[46ch] text-pretty text-[12.5px]">
        A busca cobre candidaturas, profissionais, vagas, clientes, perfis de vaga e modelos de
        avaliação. Um registo arquivado ou inativo NÃO aparece aqui — as listas mostram-no com o
        filtro próprio.
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => sairPara(SEARCH_KIND_META.application.list)}
          className="border-hair-strong bg-panel-solid text-ink rounded-pill inline-flex h-8 items-center gap-[7px] border px-[13px] text-[12.5px] font-medium"
        >
          Buscar em Candidaturas
        </button>
        <button
          type="button"
          onClick={() => sairPara(SEARCH_KIND_META.professional.list)}
          className="border-hair-strong bg-panel-solid text-ink rounded-pill inline-flex h-8 items-center gap-[7px] border px-[13px] text-[12.5px] font-medium"
        >
          Buscar em Profissionais
        </button>
      </div>
    </div>
  )

  const erroComAccao = (mensagem: string) => (
    <div className="flex flex-col items-center gap-[7px] px-[22px] pb-[30px] pt-[34px] text-center">
      <span className="text-error mb-[3px]">
        <TriangleAlert size={22} aria-hidden="true" />
      </span>
      <p className="text-ink text-base font-medium">{mensagem}</p>
      <p className="text-ink-3 max-w-[46ch] text-pretty text-[12.5px]">
        A conexão caiu ou o servidor demorou demais. O que já foi digitado continua no campo.
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => setTentativa((n) => n + 1)}
          className="border-hair-strong bg-panel-solid text-ink rounded-pill inline-flex h-8 items-center border px-[13px] text-[12.5px] font-medium"
        >
          Tentar de novo
        </button>
      </div>
    </div>
  )

  const semRecentes = (
    <div className="flex flex-col items-center gap-[7px] px-[22px] pb-[30px] pt-[34px] text-center">
      <span className="text-ink-4 mb-[3px]">
        <Clock size={22} aria-hidden="true" />
      </span>
      <p className="text-ink text-base font-medium">Comece a escrever</p>
      <p className="text-ink-3 max-w-[46ch] text-pretty text-[12.5px]">
        Os registros que abrir por aqui passam a aparecer nesta lista, para voltar a eles com dois
        toques.
      </p>
    </div>
  )

  function linhasDoGrupo(itens: SearchItem[], isRecente: boolean) {
    return itens.map((item) => {
      const idx = plana.indexOf(item)
      return (
        <SearchResultRow
          key={searchItemRef(item)}
          item={item}
          index={idx}
          active={idx === i}
          term={temTermo ? termoActivo : ''}
          isRecent={isRecente}
          onOpen={abrir}
          onForget={esquecer}
          onHover={setI}
        />
      )
    })
  }

  let corpo: ReactElement
  if (estado === 'loading') corpo = esqueleto
  else if (estado === 'error') corpo = erroComAccao('Não deu para buscar agora')
  else if (escopoFalhou && scope !== null) {
    corpo = erroComAccao(`Não deu para buscar em ${SEARCH_KIND_META[scope].label}`)
  } else if (temTermo && plana.length === 0) corpo = vazioComSaida
  else if (!temTermo && plana.length === 0) corpo = semRecentes
  else if (!temTermo) {
    corpo = (
      <div role="group" aria-labelledby="gs-grp-recentes">
        <div className="flex items-center gap-2 px-2.5 pb-[5px] pt-1.5">
          <span className="eyebrow" id="gs-grp-recentes">
            Recentes
          </span>
          <button
            type="button"
            tabIndex={-1}
            className="text-ink-4 hover:text-ink-2 hover:bg-sunk rounded-xs ml-auto px-1.5 py-0.5 text-[11px]"
            onClick={() => setRecentRefs(clearSearchHistory())}
          >
            Limpar
          </button>
        </div>
        {linhasDoGrupo(recentes, true)}
      </div>
    )
  } else {
    corpo = (
      <>
        {gruposVisiveis.map((grupo, ordem) => (
          <div key={grupo.kind} role="group" aria-labelledby={`gs-grp-${grupo.kind}`}>
            <div
              className={cn(
                'flex items-center gap-2 px-2.5 pb-[5px]',
                ordem === 0 ? 'pt-1.5' : 'pt-3',
              )}
            >
              <span className="eyebrow" id={`gs-grp-${grupo.kind}`}>
                {SEARCH_KIND_META[grupo.kind].label}
              </span>
              <span className="text-ink-4 ml-auto font-mono text-[10.5px]">
                {contagemDe(grupo)}
              </span>
            </div>
            {linhasDoGrupo(grupo.items, false)}
          </div>
        ))}
      </>
    )
  }

  const atalho = searchShortcutLabel()

  return (
    <dialog
      ref={dialogRef}
      aria-label="Busca global"
      // Ver §2 do cabeçalho: o desfoque do véu vive no `::backdrop`, e nunca no
      // mesmo elemento que `glass-float`.
      //
      // O véu é 22% de tinta e 14px de desfoque — o valor do sistema. Era 42% e
      // 3px: tinta a mais e desfoque a menos, o que apagava o que está por trás
      // em vez de o desfocar, e deixava a caixa a flutuar sobre um cinzento
      // chapado. É por trás dela que se lê que isto é vidro; sem profundidade no
      // véu, o `glass-float` fica a desfocar uma superfície já morta.
      className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-[oklch(0.16_0.02_250_/_0.22)] backdrop:backdrop-blur-[14px]"
      onClick={(e) => {
        // Clique no véu = clique no próprio `<dialog>`, que aqui ocupa o ecrã
        // inteiro por trás da caixa.
        if (e.target === dialogRef.current) close()
      }}
    >
      {isOpen ? (
        <div
          data-testid="global-search-shell"
          onKeyDown={aoTeclar}
          className="glass-float absolute left-1/2 top-[11vh] flex w-[760px] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-lg"
        >
          {/* A barra de consulta. Sem anel de foco: a caixa já está aberta à
              volta do campo, e é a linha de baixo que acende. */}
          <div className="border-hair focus-within:border-b-focus-ring flex h-[54px] shrink-0 items-center gap-[11px] border-b px-4">
            <span className="text-ink-4 grid shrink-0 place-items-center">
              <Search size={17} aria-hidden="true" />
            </span>

            {/* O escopo activo vive DENTRO da barra, antes do texto — é onde a
                pessoa está a olhar, e é o que torna o `⌫` descobrível: apagar
                para trás começa por apagar a cápsula. */}
            {scope !== null ? (
              <button
                type="button"
                aria-pressed="true"
                // O nome acessível DIZ a acção. Sem ele, o nome vinha do
                // conteúdo ("Candidaturas") e a cápsula anunciava-se como um
                // filtro a aplicar, quando o que ela faz é TIRÁ-LO.
                aria-label={`Tirar o filtro de ${SEARCH_KIND_META[scope].label}`}
                title="Tirar o filtro (⌫)"
                onClick={() => setScope(null)}
                className="border-hair-strong bg-panel-solid text-ink shadow-xs rounded-pill inline-flex h-[26px] shrink-0 items-center gap-1.5 border px-[11px] text-xs font-medium"
              >
                {SEARCH_KIND_META[scope].label}
                <X size={11} aria-hidden="true" />
              </button>
            ) : null}

            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={LISTBOX_ID}
              aria-autocomplete="list"
              aria-activedescendant={activo ? `gs-opt-${i}` : undefined}
              aria-label="Buscar em todo o Caliber"
              placeholder={
                scope !== null ? `Buscar em ${SEARCH_KIND_META[scope].label}…` : 'Buscar…'
              }
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              className="text-ink placeholder:text-ink-2 h-full min-w-0 flex-1 bg-transparent text-[16px] tracking-[-0.012em] outline-none"
            />

            <span className="bg-sunk border-hair text-ink-4 rounded-xs shrink-0 border px-1.5 py-px font-mono text-[10.5px] font-medium">
              esc
            </span>
          </div>

          <div className="grid min-h-0 grid-cols-[178px_1fr]">
            {trilho}

            <div
              ref={bodyRef}
              role="listbox"
              id={LISTBOX_ID}
              aria-label="Resultados"
              className="relative max-h-[min(58vh,520px)] overflow-y-auto p-1.5 [overscroll-behavior:contain]"
            >
              {/* Cair em silêncio é a falha que ninguém reporta. */}
              {envelope?.partial && !mostraEstado ? (
                <p className="text-ink-4 px-2.5 pb-1 pt-1.5 text-[11px]">
                  Alguns resultados podem estar faltando.
                </p>
              ) : null}
              {corpo}
            </div>
          </div>

          <div className="border-hair bg-sunk flex h-[38px] shrink-0 items-center gap-3.5 border-t px-3.5">
            <span className="text-ink-4 inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]">
              <Kbd>
                <ArrowUp size={10} aria-hidden="true" />
              </Kbd>
              <Kbd>
                <ArrowDown size={10} aria-hidden="true" />
              </Kbd>
              navegar
            </span>
            <span className="text-ink-4 inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]">
              <Kbd>
                <CornerDownLeft size={10} aria-hidden="true" />
              </Kbd>
              abrir
            </span>
            <span className="text-ink-4 inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]">
              <Kbd>{atalho.replace('K', '1')}</Kbd>
              filtrar
            </span>
            {/* O caminho do registo realçado. Não é debug: é a promessa de que
                o ENTER vai a um sítio, e de que sítio é.

                CALA-SE durante o carregamento e o erro — `activo` continua a
                apontar para o último realçado, e o rodapé anunciava um destino
                que nenhuma linha visível oferece. Uma promessa que a tela não
                pode cumprir é pior que rodapé vazio. */}
            <span
              data-testid="global-search-path"
              className="text-ink-4 ml-auto max-w-[42%] truncate font-mono text-[10.5px]"
            >
              {!mostraEstado && activo ? activo.path : ''}
            </span>
          </div>

          {/* O canal para quem não vê a lista. Sem isto, escrever num campo que
              muda uma lista silenciosa é escrever no vazio. */}
          <p aria-live="polite" className="sr-only">
            {estado === 'loading'
              ? 'Buscando…'
              : estado === 'error'
                ? 'A busca falhou.'
                : temTermo
                  ? `${plana.length} ${plana.length === 1 ? 'resultado' : 'resultados'}`
                  : `${recentes.length} recentes`}
          </p>
        </div>
      ) : null}
    </dialog>
  )
}

function Kbd({ children }: { children: ReactNode }): ReactElement {
  return (
    <span className="bg-sunk border-hair text-ink-4 rounded-xs inline-flex h-[19px] items-center border px-1.5 font-mono text-[10.5px] font-medium">
      {children}
    </span>
  )
}
